import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, Link, Typography } from '@mui/material';
import DialogHeader from 'shared/ui/DialogHeader';
import { PaperComponent } from 'shared/ui/DraggableDialog';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import usePackingListDocuments from '../../hooks/usePackingListDocuments';
import usePackingListCommands from '../../hooks/usePackingListCommands';
import usePackingListUpload from '../../hooks/usePackingListUpload';
import useLimitedInterval from '../../hooks/useLimitedInterval';
import { useOriginalDownload } from '../../hooks/usePackingListFileUrl';
import { isSuccess } from '../../lib/packingListRequest';
import { DOC_CARD_STATE, DOC_STATUS, docCardState, fromDocument, isClosedDoc, isConfirmedDoc, isEditDraft, isParsingDoc, newManualDocument, toEditDocument } from '../../lib/packingListWire';
import { clearParsedNotice, parseWatched } from '../../store/packingListSlice';
import FileDropzone from './preview/FileDropzone';
import PLPreview from './preview/PLPreview';
import PLParsingView from './preview/PLParsingView';
import PLDocumentList, { middleTruncate } from './items/PLDocumentList';
import DeleteDocumentDialog from './items/DeleteDocumentDialog';
import DocStatusChip, { EditingChip, ReadOnlyChip } from './items/DocStatusChip';
import LeaveConfirmDialog from './LeaveConfirmDialog';

export { isParsingDoc };

/** 이미 파싱 중이라는 거절은 오류가 아니다 — 결과 알림이 온다. */
const START_PARSING_QUIET = ['PARSE_IN_PROGRESS'];
const CLOSED_DOC_MESSAGE = 'Parsing gave up on this file after several attempts. Delete the document and upload the file again.';

/**
 * 헤더 경로에 보여 줄 문서 이름. 원본 파일명(가운데 줄임) → 문서 번호 → 수기 문서면 Manual entry 순이다.
 * full은 줄이기 전 이름으로, 마우스를 올리면 보여준다.
 */
const reviewDocName = (doc) => {
    if (doc.original_filename) return { label: middleTruncate(doc.original_filename), full: doc.original_filename };
    const invoiceNo = doc.invoice_no || doc.payload?.header?.invoice_no;
    if (invoiceNo) return { label: invoiceNo, full: invoiceNo };
    const label = doc.input_source === 'file' ? 'Uploaded file' : doc.id == null || doc.input_source === 'manual' ? 'Manual entry' : 'Untitled';
    return { label, full: label };
};

/** 헤더 칩 위치 보정. 굵은 14px 제목 글자와 나란히 놓이면 칩이 위로 떠 보여 살짝 내린다. */
const headerChipNudgeSx = { mt: '2px' };

/** 헤더 경로의 상위 단계(Packing List · HBL). 문서를 연 동안은 흐리게 두어 지금 보고 있는 문서 이름과 구분한다. */
const parentCrumbSx = { color: 'rgba(255,255,255,0.6)', fontWeight: 400 };


/**
 * PL 업로드·검수 다이얼로그. 상단 Upload로 열면 새 문서 시작 화면(드롭존)부터, Draft 카드로 열면 그 초안부터 연다.
 * 파일을 올리면 업로드 → 파싱 접수 → 파싱 대기 화면(원본 보기·다운로드) → 파싱이 끝나면 검수 화면 순으로 이어진다.
 * 파싱 중에는 알림 유실에 대비해 제한된 시간만 주기적으로 다시 읽고, 연결이 복구되면 바로 다시 읽는다.
 * 내가 접수한 파싱이 끝났을 때 다른 문서를 검수 중이면 화면을 바꾸지 않고 알리기만 하고, 드롭존이면 그 문서를 연다.
 * 파싱이 실패하면 빈 그리드로 열려 수기로 채우거나 Retry parsing으로 다시 접수한다. 파싱을 끝내 포기한 문서는 열지 않고 지우고 다시 올리게 한다.
 * 수기로 시작하면 서버 요청 없이 빈 검수 화면을 열고, 문서는 처음 Save Draft·Confirm할 때 만든다.
 * 하단 Save Draft·Delete·Confirm은 성공하면 창을 닫는다. 임시저장한 초안은 서버에 남아 다시 열면 저장한 내용 그대로 이어서 검수한다.
 * Save Draft 없이 창을 닫거나(X·Esc·바깥 클릭) 헤더 제목 링크로 드롭존에 돌아가려 하면, 저장하지 않은 내용이 있을 때 한 번 더 묻고 동의하면 버린다.
 * 이 창에서 올린 파일을 Draft로 한 번도 저장하지 않고 떠나면 그 문서를 지운다 — 서버가 원본 파일도 함께 지운다. 파싱을 기다리는 중에 떠나도 같다.
 * editDoc(확정 문서 카드)으로 열거나 창 안 확정 카드의 연필을 누르면 수정 모드다. 서버에 수정 초안을 만들지 않고, 확정본 헤더와 확정 품목으로 같은 검수 화면을 연다.
 * 확정본은 먼저 읽기 전용으로 열고, 하단 Edit을 누르면 수정 모드가 된다. 헤더에는 상태 칩 옆에 Editing 칩이 붙는다(초안 검수도 같다).
 * 수정 모드에서는 창을 닫으면 아무것도 저장하지 않는다 — 고친 내용은 버려지고 확정본은 그대로다. 저장은 재확정할 때만 한다.
 * 헤더에는 문서 카드와 같은 상태 칩을 붙여 문서 종류를 구분한다 — 확정본 수정은 Confirmed(초록), 초안 검수는 Draft(주황).
 */
export default function PackingListUploadDialog({ open, onClose, hbl, scope, docId: initialDocId = null, editDoc = null }) {
    const dispatch = useDispatch();
    const workspace = usePackingListDocuments(scope);
    const { documents: docs, items, allocations, allocationVersion, reload } = workspace;
    const commands = usePackingListCommands(workspace.scope);
    const uploader = usePackingListUpload(workspace.scope);
    const { download } = useOriginalDownload(commands);
    const changeSeq = useSelector((s) => s.packingList.changeSeq[workspace.key] ?? 0);
    const connected = useSelector((s) => s.socketStatus.socketStatus);
    const connectionEpoch = useSelector((s) => s.socketStatus.connectionEpoch);
    const parsedNotice = useSelector((s) => s.packingList.parsedNotice);
    /** 검수 중인 문서(서버 read/document 결과). null이면 드롭존. */
    const [reviewDoc, setReviewDoc] = useState(null);
    const [busy, setBusy] = useState(false);
    /** 확정본을 읽기 전용으로 보고 있는지. 확정 카드의 연필로 열면 보기부터 시작하고, 하단 Edit을 눌러야 수정 모드가 된다. */
    const [viewOnly, setViewOnly] = useState(false);
    /** 수정 모드에서 읽기 전용으로 돌아갈 때 올린다. 검수 화면을 새로 그려 고친 내용을 버리고 확정본 그대로 보여준다. */
    const [viewRound, setViewRound] = useState(0);
    /** 삭제 확인 창에 올라간 초안. null이면 닫힘. */
    const [deleteTarget, setDeleteTarget] = useState(null);
    /** 검수 화면이 넘겨주는 지금 상태(요청 진행 중인지·저장하지 않은 내용이 있는지·문서 version). 떠나기 전에 읽는다. */
    const leaveGuardRef = useRef(null);
    /** 이 창에서 올렸지만 아직 Draft로 저장하지 않은 문서 id. 저장 없이 떠나면 지운다. */
    const unsavedUploadIds = useRef(new Set());
    /** 떠나기 전 확인 창. { target, kind, discard } — discard는 함께 지울 업로드 문서({ id, version }). null이면 닫힘. */
    const [leavePrompt, setLeavePrompt] = useState(null);
    const [leaving, setLeaving] = useState(false);
    /** 지정된 문서를 이미 열었는지. 창 하나에 한 번만 연다. */
    const opened = useRef(false);
    /**
     * 지금 보여 줘야 할 화면의 번호. 문서를 읽기 시작할 때 올리고, 사용자가 다른 곳(드롭존·수기·다른 카드)으로 옮겨도 올린다.
     * 늦게 도착한 읽기 응답은 번호가 다르면 버린다 — 안 그러면 떠난 뒤 입력 중인 수기 화면을 옛 문서로 덮어 버린다.
     */
    const viewSeq = useRef(0);
    const showView = (doc) => {
        viewSeq.current += 1;
        // 진행 중이던 읽기는 버려지므로 그 읽기가 켜 둔 로딩 표시도 끈다.
        setBusy(false);
        setReviewDoc(doc);
    };

    /**
     * 문서를 읽어 연다. keepOnFailure면 읽기에 실패해도 보고 있던 화면을 유지한다 — 파싱 대기 화면의 재조회가 연결 끊김으로
     * 실패했다고 드롭존으로 떨어지지 않게.
     */
    const openDocument = useCallback(
        async (id, { keepOnFailure = false } = {}) => {
            viewSeq.current += 1;
            const seq = viewSeq.current;
            setBusy(true);
            const result = await commands.readDocument(id);
            setBusy(false);
            if (seq !== viewSeq.current) return;
            const doc = isSuccess(result) && result.data ? fromDocument(result.data) : null;
            if (isSuccess(result) && !result.data) dispatch(setSnackbar({ open: true, severity: 'error', message: 'The server returned no document. Reload and try again.' }));
            // 파싱을 끝내 포기한 문서는 편집할 초안이 없다. 목록으로 돌아가 지우고 다시 올리게 한다.
            if (doc && isClosedDoc(doc)) {
                dispatch(setSnackbar({ open: true, severity: 'warning', message: CLOSED_DOC_MESSAGE }));
                setReviewDoc(null);
                reload();
                return;
            }
            if (doc || !keepOnFailure) setReviewDoc(doc);
        },
        [commands, dispatch, reload]
    );

    /**
     * 확정 문서를 수정 모드로 연다. 확정본 문서(헤더·PL 하단 컨테이너 번호)를 읽고, 라인은 작업 영역이 가진 그 PL의 확정 품목으로 채운다.
     * 수정을 연 시점의 배분 버전을 함께 들고 간다 — 재확정할 때 그 사이 배분이 바뀌었는지 서버가 이 값으로 가린다.
     * 창 안의 카드에서 열다 실패하면(closeOnFailure=false) 창을 닫지 않고 문서 목록에 남는다.
     */
    const openForEdit = useCallback(
        async (target, { closeOnFailure = true } = {}) => {
            setBusy(true);
            const result = await commands.readDocument(target.id);
            setBusy(false);
            if (!isSuccess(result) || !result.data) {
                if (isSuccess(result)) dispatch(setSnackbar({ open: true, severity: 'error', message: 'The confirmed packing list could not be opened. Reload and try again.' }));
                if (closeOnFailure) onClose();
                return;
            }
            setViewOnly(true);
            setReviewDoc(toEditDocument(result.data, items.filter((i) => i.packing_list_id === target.id), allocationVersion));
        },
        [commands, dispatch, onClose, items, allocationVersion]
    );

    /**
     * 작업 영역의 Draft 카드로 열었으면(docId 지정) 그 초안을 바로 연다.
     * 상단 Upload로 열면(docId 없음) 저장된 초안이 있어도 새 문서 시작 화면(드롭존)부터 연다 — 초안은 그 아래 카드 목록에서 이어서 연다.
     */
    // 수정 모드는 확정 품목으로 라인을 채우므로 작업 영역을 다 읽은 뒤에 연다.
    const workspaceReady = workspace.status === 'ready';
    useEffect(() => {
        if (!open || opened.current || initialDocId == null) return;
        if (editDoc && !workspaceReady) return;
        opened.current = true;
        if (editDoc) openForEdit(editDoc);
        else openDocument(initialDocId);
    }, [open, initialDocId, editDoc, workspaceReady, openDocument, openForEdit]);

    /** 파싱 중인 문서는 변경 알림·연결 복구·주기 재조회로 다시 읽어, 초안이 되면 검수로 넘어간다. 주기 재조회 상한이 지나면 Check again으로 담당자가 묻는다. */
    const parsing = isParsingDoc(reviewDoc);
    const parsingDocId = parsing ? reviewDoc.id : null;
    useEffect(() => {
        if (parsingDocId != null) openDocument(parsingDocId, { keepOnFailure: true });
        // 알림·연결 복구 때만. 나머지는 그 시점의 값을 쓴다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeSeq, connectionEpoch]);
    const poll = useLimitedInterval({
        enabled: parsingDocId != null && Boolean(connected),
        resetKey: `${parsingDocId}:${changeSeq}:${connectionEpoch}`,
        onTick: () => openDocument(parsingDocId, { keepOnFailure: true }),
    });

    /** 파싱 대기가 끝나면 카드 배지(Parsing → Draft)도 맞도록 작업 영역을 다시 읽는다. */
    const wasParsing = useRef(false);
    useEffect(() => {
        if (wasParsing.current && !parsing) reload();
        wasParsing.current = parsing;
    }, [parsing, reload]);

    const step = busy && !reviewDoc ? 'loading' : reviewDoc ? (parsing ? 'parsing' : 'review') : 'drop';
    const wide = step === 'review' || step === 'parsing';

    /** 전송 중이거나, 전송은 끝났지만 아직 문서를 여는 중. 그동안 드롭존·카드·수기 입력을 막는다. */
    const uploading = uploader.status === 'uploading' || uploader.status === 'done';

    /**
     * 내가 접수한 파싱이 끝났다. 드롭존이면 그 문서를 열어 검수를 시작하고,
     * 다른 문서를 검수 중이거나 업로드·삭제·떠나기 확인 중이면 화면을 바꾸지 않고 카드에서 열라고만 알린다.
     */
    useEffect(() => {
        if (!parsedNotice || parsedNotice.key !== workspace.key) return;
        dispatch(clearParsedNotice());
        if (reviewDoc?.id === parsedNotice.docId) return;
        const name = parsedNotice.filename ?? 'the uploaded file';
        if (step === 'drop' && !uploading && !deleteTarget && !leavePrompt && !busy) {
            openDocument(parsedNotice.docId);
            return;
        }
        const message = parsedNotice.parseResult === 'failed' ? `Parsing failed for ${name}. Open it from the document cards to enter the lines manually.` : `Parsing finished for ${name}. Open it from the document cards when you are done here.`;
        dispatch(setSnackbar({ open: true, severity: parsedNotice.parseResult === 'failed' ? 'warning' : 'info', message }));
        // 알림이 올 때만 한 번. 나머지는 그 시점의 화면 상태다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsedNotice]);

    /**
     * 파싱 접수. 받아들여지면(또는 이미 파싱 중이면) 결과 알림을 기다리는 문서로 기억하고, 그 문서를 보고 있으면 바로 파싱 대기 화면으로 바꾼다
     * — 후속 조회가 실패해도 대기 화면에서 폴링·알림이 이어지게. 그 밖의 거절은 안내만 하고 문서는 연다.
     */
    const requestParsing = async ({ id, version, parseGeneration, filename }) => {
        const result = await commands.startParsing({ id, version, parseGeneration, quietCodes: START_PARSING_QUIET });
        if (!isSuccess(result) && result.code !== 'PARSE_IN_PROGRESS') return result;
        const accepted = result.data ?? {};
        dispatch(parseWatched({ docId: id, key: workspace.key, filename, generation: accepted.parse_generation ?? null }));
        setReviewDoc((current) =>
            current?.id === id
                ? { ...current, upload_status: 'parsing', parse_result: null, upload_id: accepted.upload_id ?? current.upload_id, version: accepted.version ?? current.version, parse_generation: accepted.parse_generation ?? current.parse_generation }
                : current
        );
        return result;
    };

    /** 검수 화면의 Retry parsing. 접수되면 파싱 대기 화면으로 돌아가고, 카드와 문서를 서버 값으로 맞춘다. */
    const retryParsing = async ({ id, version, parseGeneration, filename = null }) => {
        const result = await requestParsing({ id, version, parseGeneration, filename });
        if (isSuccess(result) || result.code === 'PARSE_IN_PROGRESS') {
            reload();
            await openDocument(id, { keepOnFailure: true });
        }
        return result;
    };

    /**
     * 업로드 201 뒤처리. 서버에 문서가 생겼으니 파싱을 접수하고(start_parsing) 카드 목록을 갱신한 뒤 그 문서를 연다.
     * 접수가 거절돼도 문서는 연다 — 원본 보기·다운로드는 접수와 무관하다.
     * 거절(413·415 등)·응답 없음은 드롭존 위 안내로 남고, 응답 없음·서버 오류는 같은 키로 Retry할 수 있다.
     * 문서가 열릴 때까지 업로드 상태(done)를 유지해 드롭존이 두 번째 파일을 받지 않게 하고, 그 뒤에 비운다.
     */
    const openUploaded = async (result, filename = null) => {
        if (!result?.ok) return;
        const { document_id: documentId, version, parse_generation: parseGeneration } = result.data ?? {};
        if (documentId == null) {
            dispatch(setSnackbar({ open: true, severity: 'error', message: 'The upload succeeded but the server returned no document id. Reload and check the document cards.' }));
            reload();
            uploader.reset();
            return;
        }
        unsavedUploadIds.current.add(documentId);
        await requestParsing({ id: documentId, version, parseGeneration, filename });
        reload();
        await openDocument(documentId);
        uploader.reset();
    };
    // 파일명은 인자로 넘긴다. 업로드 상태의 파일은 이 시점에 아직 비어 있다.
    const handleFile = async (file) => openUploaded(await uploader.upload(file), file?.name ?? null);
    const handleRetry = async () => openUploaded(await uploader.retry(), uploader.file?.name ?? null);
    const editing = Boolean(editDoc) || isEditDraft(reviewDoc);

    /** 수기 등록은 서버 요청 없이 빈 검수 화면부터 연다. 문서는 처음 Save Draft·Confirm할 때 만들어진다. */
    const startManual = () => showView(newManualDocument());

    /**
     * 드롭존 아래 문서 카드. 카드를 누르든 연필을 누르든 같다 — 초안 카드는 바로 검수(또는 파싱 대기)로,
     * 확정 카드는 읽기 전용 보기로 연다(하단 Edit으로 수정 모드).
     * 업로드 중에는 열지 않는다 — 열어서 편집하는 사이 업로드가 끝나면 새 문서로 바뀌며 편집이 사라진다.
     * 파싱을 끝내 포기한 문서(Failed)는 열 수 없다 — 지우고 다시 올리도록 삭제 확인으로 넘긴다.
     */
    const openCardDoc = (target) => {
        if (uploading || !target) return;
        if (isConfirmedDoc(target)) openForEdit(target, { closeOnFailure: false });
        else if (docCardState(target) === DOC_CARD_STATE.FAILED) {
            dispatch(setSnackbar({ open: true, severity: 'warning', message: CLOSED_DOC_MESSAGE }));
            setDeleteTarget(target);
        } else openDocument(target.id);
    };
    const openCard = (id) => openCardDoc(docs.find((d) => d.id === id));

    const confirmDelete = async () => {
        setBusy(true);
        const result = await commands.deleteDocument({ id: deleteTarget.id, version: deleteTarget.version, allocationVersion });
        setBusy(false);
        if (!isSuccess(result)) return;
        if (reviewDoc?.id === deleteTarget.id) showView(null);
        setDeleteTarget(null);
        reload();
    };

    /** 검수 화면을 떠난다. 'close'면 창을 닫고, 'other'면 문서 목록(드롭존)으로 돌아간다. 만들어진 카드가 보이도록 목록을 다시 읽는다. */
    const leaveReview = (target) => {
        if (target === 'close') {
            closeReview();
            return;
        }
        leaveGuardRef.current = null;
        showView(null);
        reload();
    };

    /** 떠나도 되는 화면이면 바로 떠난다. 검수·파싱 대기 화면이 아니면 닫기만 한다. */
    const completeLeave = (target) => {
        if (target === 'view') {
            setViewOnly(true);
            setViewRound((n) => n + 1);
            return;
        }
        if (target === 'close' && (!reviewDoc || parsing)) onClose();
        else leaveReview(target);
    };

    /**
     * 떠나기 전에 물어야 할 것. null이면 저장·확정이 진행 중이라 떠나지 않는다 — 그 요청이 끝나며 창을 닫거나 결과를 보여준다.
     * 이 창에서 올린 문서를 아직 Draft로 저장하지 않았으면 업로드째 버릴지 묻고, 저장된 문서에 고친 내용이 있으면 그 내용을 버릴지 묻는다.
     */
    const leavePlan = () => {
        if (!reviewDoc) return {};
        const state = parsing ? null : leaveGuardRef.current?.();
        if (state?.pending) return null;
        if (reviewDoc.id != null && unsavedUploadIds.current.has(reviewDoc.id) && !state?.persisted) {
            return { kind: 'upload', discard: { id: reviewDoc.id, version: state?.version ?? reviewDoc.version } };
        }
        if (state?.dirty) return { kind: editing ? 'edit' : 'changes' };
        return {};
    };

    /**
     * 검수 화면을 떠나려 한다(X·바깥 클릭·Esc로 닫기, 헤더 제목 링크, 수정 모드의 Read-only로 확정본 보기 복귀). 저장하지 않은 내용이 있으면 확인 창을 띄운다.
     * 업로드 중에 닫으면 전송을 끊는다 — 서버에 이미 등록됐다면 문서는 카드로 남는다.
     */
    const requestLeave = (target) => {
        if (uploading) uploader.reset();
        const plan = leavePlan();
        if (!plan) return;
        if (plan.kind) setLeavePrompt({ target, ...plan });
        else completeLeave(target);
    };
    const requestClose = () => requestLeave('close');

    /** 확인 창에서 떠나기로 했다. 저장하지 않은 업로드면 문서를 지우고(서버가 원본 파일도 지운다) 떠난다. 지우지 못해도 떠난다 — 문서는 카드로 남아 직접 지울 수 있다. */
    const confirmLeave = async () => {
        const { target, discard } = leavePrompt;
        if (discard) {
            setLeaving(true);
            await commands.deleteDocument({ id: discard.id, version: discard.version, allocationVersion });
            setLeaving(false);
            unsavedUploadIds.current.delete(discard.id);
        }
        setLeavePrompt(null);
        completeLeave(target);
    };

    /** 저장·확정·삭제가 끝나면 작업 영역을 다시 읽어 카드·아이템이 서버 상태와 맞게 한다. 저장된 업로드는 더 이상 떠날 때 지울 대상이 아니다. */
    const closeReview = () => {
        if (reviewDoc?.id != null) unsavedUploadIds.current.delete(reviewDoc.id);
        reload();
        onClose();
    };

    /** 헤더 제목. 검수·파싱 화면에서는 문서 목록(드롭존)으로 돌아가는 링크가 된다 — 수정 모드는 돌아갈 목록이 없어 글자만 둔다. */
    const headerTitle = `Packing List · ${hbl.hbl || '—'}`;
    /** 제목 뒤 경로(› 문서 이름). 문서 상태는 그 뒤의 칩이 보여준다. */
    const crumb = reviewDoc ? reviewDocName(reviewDoc) : null;

    return (
        <Dialog open={open} onClose={requestClose} maxWidth={wide ? 'xl' : docs.length ? 'md' : 'sm'} fullWidth PaperComponent={PaperComponent} PaperProps={{ sx: { height: wide ? '92vh' : 'auto' } }}>
            <DialogHeader
                cursor
                sx={{ height: '56px' }}
                title={
                    <>
                        {wide && !editing ? (
                            <Link
                                component="button"
                                color="inherit"
                                underline="always"
                                title="Back to the document list"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={() => requestLeave('other')}
                                sx={{ font: 'inherit', verticalAlign: 'baseline', ...(crumb && parentCrumbSx), '&:hover': { color: 'white' } }}
                            >
                                {headerTitle}
                            </Link>
                        ) : (
                            <Box component="span" sx={crumb ? parentCrumbSx : undefined}>
                                {headerTitle}
                            </Box>
                        )}
                        {crumb && (
                            <Box component="span" title={crumb.full} sx={{ whiteSpace: 'nowrap' }}>
                                <Box component="span" sx={{ mx: 0.75, color: 'rgba(255,255,255,0.6)', fontWeight: 400 }}>›</Box>
                                {crumb.label}
                            </Box>
                        )}
                    </>
                }
                onClose={requestClose}
                leftChildren={
                    editing || step === 'review' ? (
                        <>
                            <DocStatusChip status={editing ? DOC_STATUS.CONFIRMED : DOC_STATUS.DRAFT} sx={{ ml: 1.5, ...headerChipNudgeSx }} />
                            {editing && viewOnly ? <ReadOnlyChip sx={{ ml: 0.75, ...headerChipNudgeSx }} /> : <EditingChip sx={{ ml: 0.75, ...headerChipNudgeSx }} />}
                        </>
                    ) : null
                }
            />
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: wide ? 'white' : '#fafafa' }}>
                {step === 'loading' && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {step === 'drop' && (
                    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <FileDropzone onFile={handleFile} disabled={busy} uploading={uploading} progress={uploader.progress} filename={uploader.file?.name ?? ''} />
                        {uploader.status === 'error' && (
                            <Alert
                                severity="error"
                                onClose={uploader.reset}
                                action={uploader.retryable ? <Button color="inherit" size="small" onClick={handleRetry} sx={{ fontSize: 12 }}>Retry</Button> : undefined}
                                sx={{ fontSize: 12, py: 0, alignItems: 'center' }}
                            >
                                {uploader.file?.name ? <b style={{ marginRight: 6 }}>{uploader.file.name}</b> : null}{uploader.error}
                            </Alert>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ flex: 1, borderTop: '1px solid #c7c7c7' }} />
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>or</Typography>
                            <Box sx={{ flex: 1, borderTop: '1px solid #c7c7c7' }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button size="small" variant="outlined" onClick={startManual} disabled={busy || uploading} sx={{ fontSize: 12, bgcolor: 'white', '&:hover': { bgcolor: 'white' } }}>Enter manually</Button>
                        </Box>
                        {docs.length > 0 && (
                            <Box sx={{ pt: 1.5, borderTop: '1px solid #e6ebee' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#01354d', mb: 0.75 }}>Uploaded documents</Typography>
                                <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>Click a card to open it or use the trash icon to delete it.</Typography>
                                <PLDocumentList docs={docs} selectedId={null} onSelect={openCard} onDelete={(id) => setDeleteTarget(docs.find((d) => d.id === id))} deleteConfirmed onDownload={download} onEdit={openCardDoc} onEditDraft={openCardDoc} />
                            </Box>
                        )}
                    </Box>
                )}
                {step === 'parsing' && <PLParsingView key={reviewDoc.id} doc={reviewDoc} commands={commands} onClose={requestClose} pollExhausted={poll.exhausted} onCheckAgain={poll.restart} />}
                {step === 'review' && (
                    <PLPreview
                        key={`${reviewDoc.id ?? 'new'}:${viewRound}:${reviewDoc.parse_generation ?? 0}`}
                        hbl={hbl}
                        doc={reviewDoc}
                        workspaceKey={workspace.key}
                        confirmedDocs={docs.filter(isConfirmedDoc)}
                        confirmedItems={items.filter((i) => i.packing_list_id === reviewDoc.id)}
                        allocations={allocations}
                        allocationVersion={allocationVersion}
                        commands={commands}
                        onConfirmed={closeReview}
                        onDraftSaved={closeReview}
                        onDeleted={closeReview}
                        onEditCancelled={closeReview}
                        leaveGuardRef={leaveGuardRef}
                        onVersionChanged={reload}
                        readOnly={editing && viewOnly}
                        onStartEdit={() => setViewOnly(false)}
                        onBackToView={() => requestLeave('view')}
                        onRetryParsing={retryParsing}
                    />
                )}
            </DialogContent>
            <LeaveConfirmDialog kind={leavePrompt?.kind ?? null} busy={leaving} onCancel={() => setLeavePrompt(null)} onConfirm={confirmLeave} />
            <DeleteDocumentDialog doc={deleteTarget} busy={busy} onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
        </Dialog>
    );
}
